/** Per-event play log entry kinds. */
export type PlayKind =
  | '2pt_made'
  | '2pt_miss'
  | '3pt_made'
  | '3pt_miss'
  | 'ft_made'
  | 'ft_miss'
  | 'reb_off'
  | 'reb_def'
  | 'ast'
  | 'stl'
  | 'blk'
  | 'foul_personal'
  | 'foul_technical'
  | 'sub_in'
  | 'sub_out';

export const PLAY_KIND_LABEL: Record<PlayKind, string> = {
  '2pt_made': '2-pt made',
  '2pt_miss': '2-pt miss',
  '3pt_made': '3-pt made',
  '3pt_miss': '3-pt miss',
  ft_made: 'FT made',
  ft_miss: 'FT miss',
  reb_off: 'O-Reb',
  reb_def: 'D-Reb',
  ast: 'AST',
  stl: 'STL',
  blk: 'BLK',
  foul_personal: 'Foul',
  foul_technical: 'Tech',
  sub_in: 'Sub In',
  sub_out: 'Sub Out'
};

/** Points value of a play kind, or 0 for non-scoring kinds. */
export const PLAY_POINTS: Record<PlayKind, number> = {
  '2pt_made': 2,
  '2pt_miss': 0,
  '3pt_made': 3,
  '3pt_miss': 0,
  ft_made: 1,
  ft_miss: 0,
  reb_off: 0,
  reb_def: 0,
  ast: 0,
  stl: 0,
  blk: 0,
  foul_personal: 0,
  foul_technical: 0,
  sub_in: 0,
  sub_out: 0
};

export type PlayTeam = 'home' | 'away';

export interface PlayEvent {
  id: string;
  athleteId: string;
  team: PlayTeam;
  /** 1-based quarter (or overtime period). */
  quarter: number;
  /** Seconds remaining on the game clock when the play occurred. */
  clockSec: number;
  kind: PlayKind;
  /**
   * Flagrant tier for foul plays — `1` = unnecessary contact, `2` = excessive (ejection).
   * Only meaningful when `kind === 'foul_personal'`. Absent on non-foul plays.
   */
  flagrant?: 1 | 2;
  /** ISO timestamp of when the play was recorded. */
  at: string;
}

export interface BasketballGameClock {
  /** Length of one quarter in seconds. */
  quarterDurationSec: number;
  /** Seconds remaining in the current quarter. */
  remainingSec: number;
  /** True while the clock is counting down. */
  running: boolean;
  /** 1-based current quarter (or overtime period 5+). */
  currentQuarter: number;
  /** Wall-clock timestamp from the most recent tick (ISO). */
  lastTickAt: string | null;
  /** Shot-clock seconds (24 NBA/FIBA, 30 NCAA, 35 HS). Display only — adapter does not enforce. */
  shotClockSec?: number;
}

export interface BasketballPlayerLine {
  /** Stable ID of the per-quarter line for editing. */
  id: string;
  athleteId: string;
  /** 1-based quarter index (1..4 typically). */
  quarter: number;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  fouls: number;
  minutesPlayed: number;
}

export interface BasketballGame {
  /** Mirrors the parent ScoringSession id. */
  sessionId: string;
  /** Opposing team name (free text — away team rosters are not modelled). */
  opponent: string;
  quarterCount: number; // 2 or 4
  /** Legacy aggregate per-quarter rows (kept for back-compat). */
  lines: BasketballPlayerLine[];
  /** Per-event play log — the source of truth for scoring + box stats. */
  plays?: PlayEvent[];
  /** Live game clock state. */
  clock?: BasketballGameClock;
  /** Athlete IDs currently on court (home roster). */
  onCourt?: string[];
  /** Personal-foul ceiling per league (5 FIBA/NCAA/WNBA/HS, 6 NBA). Live scorer grays out fouled-out players at this count. */
  foulOutLimit?: number;
}

export const BASKETBALL_POSITIONS = [
  { value: 'PG', label: 'Point Guard' },
  { value: 'SG', label: 'Shooting Guard' },
  { value: 'SF', label: 'Small Forward' },
  { value: 'PF', label: 'Power Forward' },
  { value: 'C', label: 'Center' }
] as const;

export type BasketballPosition = (typeof BASKETBALL_POSITIONS)[number]['value'];

/** Aggregated per-player box-score line derived from the play log. */
export interface BoxScoreLine {
  athleteId: string;
  fgMade: number;
  fgAtt: number;
  threePtMade: number;
  threePtAtt: number;
  ftMade: number;
  ftAtt: number;
  rebOff: number;
  rebDef: number;
  rebTotal: number;
  ast: number;
  stl: number;
  blk: number;
  pf: number;
  points: number;
}

export function emptyBoxLine(athleteId: string): BoxScoreLine {
  return {
    athleteId,
    fgMade: 0,
    fgAtt: 0,
    threePtMade: 0,
    threePtAtt: 0,
    ftMade: 0,
    ftAtt: 0,
    rebOff: 0,
    rebDef: 0,
    rebTotal: 0,
    ast: 0,
    stl: 0,
    blk: 0,
    pf: 0,
    points: 0
  };
}
